// ============================================================================
// chatBridge.ts — МОСТ к функциям ЯДРА чата (SSoT/SRP). Луна: «отправка/статусы/
// контекст-привязка — в ядре, работают даже если UI чата упал/не смонтирован».
//
// Используется ЛЮБЫМ UI чата: полноэкранное приложение (AppHost, sdk.messenger)
// И контекстные панели у объектов (ContextChatPanel вне AppHost — но те же
// функции ядра через этот мост). Один механизм — без дублирования.
//
// Пока мост = тонкая обёртка над getSDK().messenger RPC (реальные функции ядра);
// при переводе AppHost-чата на sdk.messenger — тот же набор, источники сходятся.
// ============================================================================

import type { Conversation } from '@zdc/sdk';
import { getSDK } from '@/store/sdkStore';
import { createLogger } from '@/lib/logger';
import { readThrough } from '@/lib/offlineCache';

const log = createLogger('chatBridge');

export interface ContextThreadRef {
  objectType: string;
  objectCode: string;
  objectId: string;
  title?: string;
}

/** Получить/создать ОБСУЖДЕНИЕ объекта (один чат на объект, идемпотентно).
 *  Функция ЯДРА (см. sdk.messenger.getOrCreateContextThread) — приложение/панель
 *  не знают деталей list+create. */
export async function getOrCreateContextThread(ctx: ContextThreadRef): Promise<{ conversationId: string }> {
  const list = await getSDK().messenger.listConversations({ contextType: ctx.objectType, contextId: ctx.objectId });
  const items = ((list as any)?.items || []) as any[];
  if (items.length > 0) {
    return { conversationId: String(items[0].id || '') };
  }
  const cr = await getSDK().messenger.createConversation({
    kind: 'group', title: ctx.title || '', memberIds: [],
    contextType: ctx.objectType, contextCode: ctx.objectCode, contextId: ctx.objectId,
  } as any);
  const convId = String((cr as any)?.conversation?.id || (cr as any)?.id || '');
  if (!convId) {
    log.warn('createConversation вернул пустой id', { context: ctx });
    throw new Error('Не удалось создать обсуждение объекта');
  }
  return { conversationId: convId };
}

/** Отметить диалог прочитанным (снимает бейджи у других приложений; событие chat.read). */
export async function markContextRead(conversationId: string): Promise<void> {
  if (!conversationId) return;
  await getSDK().messenger.markConversationRead({ conversationId } as any);
}

// ---------- Функции ядра чата для UI (лента/действия) ----------
// Единый слой: полноэкранное приложение чата И контекстные панели используют
// ОДИН механизм (см. sdk.messenger). Offline-отправка — chatQueue (SSoT).

export async function listMessages(conversationId: string, limit = 200): Promise<any[]> {
  // ОФЛАЙН-ФЁРСТ: последняя успешная лента диалога сохраняется (offlineCache);
  // при обрыве связи открывается из сохранённого (баннер в ChatThread).
  const res = await readThrough(`chat:messages:${conversationId}`, async () => {
    const r = await getSDK().messenger.listMessages({ conversationId, limit });
    return ((r as any)?.items || []) as any[];
  });
  return res.data;
}

export async function listMessagesBefore(conversationId: string, beforeTs: string, limit = 100): Promise<any[]> {
  const r = await getSDK().messenger.listMessages({ conversationId, beforeTs, limit });
  return ((r as any)?.items || []) as any[];
}

/** markRead — прочтение диалога (ядро; событие chat.read снимает бейджи других). */
export async function markRead(conversationId: string): Promise<void> {
  if (!conversationId) return;
  await getSDK().messenger.markConversationRead({ conversationId } as any);
}

export async function reactToMessage(conversationId: string, messageId: string, emoji: string): Promise<void> {
  await getSDK().messenger.reactToMessage({ conversationId, messageId, emoji } as any);
}

export async function forwardMessage(fromConversationId: string, messageId: string, toConversationId: string): Promise<void> {
  await getSDK().messenger.forwardMessage({ fromConversationId, messageId, toConversationId } as any);
}

/** Удаление сообщения (скоуп: 'self' — скрыть у себя (режим выбора),
 *  'all' — пометка «у всех», 'permanent' — физическое (только админ)).
 *  Права проверяет СЕРВЕР — как у одиночного удаления. */
export async function deleteMessage(conversationId: string, messageId: string, scope = 'self'): Promise<void> {
  await getSDK().messenger.deleteMessage({ conversationId, messageId, scope } as any);
}

export async function clearConversation(conversationId: string, scope: string): Promise<void> {
  await getSDK().messenger.clearConversation({ conversationId, scope } as any);
}

// ---------- Закреплённые сообщения (D6; SSoT — сервер) ----------

/** Закрепить/открепить сообщение (участник чата; идемпотентно). Событие
 *  chat.message.pinned.{convId} обновляет ленту и панель точечно. */
export async function pinMessage(conversationId: string, messageId: string, pinned: boolean): Promise<void> {
  await (getSDK().messenger as any).pinMessage({ conversationId, messageId, pinned });
}

/** Закреплённые сообщения чата (новые первыми; скрытые «у меня» не приходят). */
export async function listPinnedMessages(conversationId: string, limit = 30, offset = 0): Promise<any[]> {
  const r = await (getSDK().messenger as any).listPinnedMessages({ conversationId, limit, offset });
  return ((r as any)?.items || []) as any[];
}

// ---------- Список чатов / создание / участники (ядро; UI не знает деталей) ----------

/** Все диалоги пользователя (список чатов; непрочитанные — в поле unreadCount).
 *  ОФЛАЙН-ФЁРСТ: список сохраняется; при обрыве связи открывается из кэша. */
export async function listConversations(filters?: { contextType?: string; contextId?: string; kind?: string }): Promise<any[]> {
  const key = `chat:conversations:${filters?.contextType || ''}:${filters?.contextId || ''}:${filters?.kind || ''}`;
  const res = await readThrough(key, async () => {
    const r = await getSDK().messenger.listConversations((filters || {}) as any);
    return ((r as any)?.items || []) as any[];
  });
  return res.data;
}

/** Создать диалог (kind: direct|group; контекст для обсуждения объекта). */
export async function createConversation(opts: {
  kind: 'direct' | 'group';
  title?: string;
  memberIds?: string[];
  contextType?: string;
  contextCode?: string;
  contextId?: string;
}): Promise<{ id: string }> {
  const r = await getSDK().messenger.createConversation(opts as any);
  const id = String((r as any)?.conversation?.id || (r as any)?.id || '');
  if (!id) throw new Error('Не удалось создать чат (пустой id)');
  return { id };
}

/** Диалог по id (участники/пресенс/контекст). ОФЛАЙН-ФЁРСТ: последний
 *  успешный ответ сохраняется — правая панель «О чате» работает без связи. */
export async function getConversation(id: string): Promise<Conversation> {
  const res = await readThrough<Conversation>(`chat:conversation:${id}`, async () => {
    const r = await getSDK().messenger.getConversation({ id });
    if (!r.conversation) throw new Error('Диалог не найден');
    return r.conversation;
  });
  return res.data;
}

/** Добавить участника в групповой чат (любой участник; идемпотентно).
 *  Возвращает АКТУАЛЬНЫЙ диалог (SSoT — сервер): панель «О чате» обновляется
 *  сразу, остальным состав приходит событием chat.conversation.updated.{id}. */
export async function addConversationMember(conversationId: string, memberId: string): Promise<Conversation | null> {
  const r = await getSDK().messenger.addConversationMember({ conversationId, memberId });
  return r.conversation ?? null;
}

/** Удалить участника из группы (право: создатель чата или администратор;
 *  создателя удалить нельзя). Идемпотентно (повтор — без события/уведомления). */
export async function removeConversationMember(conversationId: string, memberId: string): Promise<Conversation | null> {
  const r = await getSDK().messenger.removeConversationMember({ conversationId, memberId });
  return r.conversation ?? null;
}

/** Покинуть групповой чат (себя; последний участник выйти не может; создатель
 *  выходит — права создателя переходят первому оставшемуся — SSoT сервера). */
export async function leaveConversation(conversationId: string): Promise<Conversation | null> {
  const r = await getSDK().messenger.leaveConversation({ conversationId });
  return r.conversation ?? null;
}

/** Переименовать группу (право: создатель чата или администратор; direct — нельзя,
 *  название обязательно, до 200 символов — правила сервера). */
export async function setConversationTitle(conversationId: string, title: string): Promise<Conversation | null> {
  const r = await getSDK().messenger.setConversationTitle({ conversationId, title });
  return r.conversation ?? null;
}

// ---------- Конверсия сообщения в заявку/задачу (§2.3, «Object-Centric Chat») ----------

/** Созданный из сообщения объект (связь конверсии хранит СЕРВЕР). */
export interface ConvertedMessageObject {
  /** 'order' | 'task' (SSoT — ответ сервера) */
  objectType: string;
  objectId: string;
  /** код типа созданного объекта (для навигации/печати) */
  typeCode: string;
  title: string;
}

/** Конверсия сообщения в заявку/задачу (ядро — сервер: права/типы/связь).
 *  typeCode '' — единственный существующий тип; title '' — тема из текста
 *  сообщения; assigneeId — только для kind='task' ('' = пул задач). */
export async function convertMessage(opts: {
  conversationId: string;
  messageId: string;
  kind: 'order' | 'task';
  typeCode?: string;
  title?: string;
  assigneeId?: string;
}): Promise<ConvertedMessageObject> {
  const r = await getSDK().messenger.convertMessage({
    conversationId: opts.conversationId,
    messageId: opts.messageId,
    kind: opts.kind,
    typeCode: opts.typeCode || '',
    title: opts.title || '',
    assigneeId: opts.assigneeId || '',
    extra: {},
  });
  return { objectType: r.objectType, objectId: r.objectId, typeCode: r.typeCode, title: r.title };
}
